import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import Layout from '../components/Layout';
import { useAuth } from '../auth/AuthContext';
import { usePractice } from '../context/PracticeContext';
import axios from '../utils/axiosInstance';

// Define the interrupt function for d3-zoom to fix wheel zoom error
window.interrupt = function() { return true; };

// Node components
function IdentifierNode({ data, isConnectable }) {
  return (
    <div className="custom-node identifier-node">
      <div className="node-header">Identifier</div>
      <div className="node-content">
        <input
          className="form-input"
          placeholder="Name"
          value={data.name}
          onChange={(e) => data.onChange(e.target.value, 'name', data.id)}
        />
        <textarea
          className="form-input"
          placeholder="Description"
          value={data.description}
          onChange={(e) => data.onChange(e.target.value, 'description', data.id)}
          rows={2}
        />
        {isConnectable && (
          <div className="node-ports">
            <div className="node-port-out" title="Connect to next node" />
          </div>
        )}
      </div>
    </div>
  );
}

function ProcessNode({ data, isConnectable }) {
  return (
    <div className="custom-node process-node">
      <div className="node-header">Process Step</div>
      <div className="node-content">
        <input
          className="form-input"
          placeholder="Name"
          value={data.name}
          onChange={(e) => data.onChange(e.target.value, 'name', data.id)}
        />
        <select
          className="form-input"
          value={data.action}
          onChange={(e) => data.onChange(e.target.value, 'action', data.id)}
        >
          <option value="">Select Action</option>
          <option value="get_info">Get Information</option>
          <option value="set_appointment">Set Appointment</option>
          <option value="change_appointment">Change Appointment</option>
          <option value="cancel_appointment">Cancel Appointment</option>
          <option value="create_task">Create Task</option>
        </select>
        {isConnectable && (
          <div className="node-ports">
            <div className="node-port-in" title="Connect from previous node" />
            <div className="node-port-out" title="Connect to next node" />
          </div>
        )}
      </div>
    </div>
  );
}

function RequestInfoNode({ data, isConnectable }) {
  return (
    <div className="custom-node request-info-node">
      <div className="node-header">Request Info</div>
      <div className="node-content">
        <input
          className="form-input"
          placeholder="Info to Request"
          value={data.infoName}
          onChange={(e) => data.onChange(e.target.value, 'infoName', data.id)}
        />
        <input
          className="form-input"
          placeholder="Question to Ask"
          value={data.question}
          onChange={(e) => data.onChange(e.target.value, 'question', data.id)}
        />
        <select
          className="form-input"
          value={data.required}
          onChange={(e) => data.onChange(e.target.value === 'true', 'required', data.id)}
        >
          <option value="true">Required</option>
          <option value="false">Optional</option>
        </select>
        {isConnectable && (
          <div className="node-ports">
            <div className="node-port-in" title="Connect from previous node" />
            <div className="node-port-out" title="Connect to next node" />
          </div>
        )}
      </div>
    </div>
  );
}

function DatabaseNode({ data, isConnectable }) {
  return (
    <div className="custom-node database-node">
      <div className="node-header">Database</div>
      <div className="node-content">
        <select
          className="form-input"
          value={data.dbType}
          onChange={(e) => data.onChange(e.target.value, 'dbType', data.id)}
        >
          <option value="">Select DB Type</option>
          <option value="calendar">Calendar</option>
          <option value="practice_info">Practice Info</option>
          <option value="tasks">Tasks</option>
        </select>
        <input
          className="form-input"
          placeholder="Operation"
          value={data.operation}
          onChange={(e) => data.onChange(e.target.value, 'operation', data.id)}
        />
        {isConnectable && (
          <div className="node-ports">
            <div className="node-port-in" title="Connect from previous node" />
          </div>
        )}
      </div>
    </div>
  );
}

// Custom node types
const nodeTypes = {
  identifier: IdentifierNode,
  process: ProcessNode,
  requestInfo: RequestInfoNode,
  database: DatabaseNode,
};

// Initial nodes and edges
const initialNodes = [
  {
    id: '1',
    type: 'identifier',
    position: { x: 250, y: 100 },
    data: {
      name: 'Call Intent',
      description: 'Identify what the caller wants',
      onChange: () => {},
      id: '1'
    },
  },
];

const initialEdges = [];

const CallFlowPage = () => {
  const { keycloak } = useAuth();
  // practiceId now comes from PracticeContext — replaces the local
  // userId → practiceId resolution chain that previously lived here.
  const { practiceId, loading: practiceLoading } = usePractice();
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [nodeCount, setNodeCount] = useState(1);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [flowName, setFlowName] = useState('');
  const [flowDescription, setFlowDescription] = useState('');
  const [saveStatus, setSaveStatus] = useState({ saved: false, message: '' });
  const [flows, setFlows] = useState([]);
  const [showFlowSelector, setShowFlowSelector] = useState(false);
  const [flowId, setFlowId] = useState(null);
  
  // Add state for loading and error handling
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get current user ID for localStorage backup keys (practiceId comes from context).
  const [userId, setUserId] = useState(null);

  // Get user and associated practice from Keycloak or fallback
  useEffect(() => {
    console.log("Keycloak state:", keycloak?.authenticated, keycloak?.tokenParsed?.sub);
    
    // Reference to track if an alert has been shown already
    let errorAlertShown = false;
    
    // Only use test user if there's no Keycloak authentication
    if (!keycloak?.authenticated || !keycloak?.tokenParsed?.sub) {
      console.warn("No Keycloak authentication found, using test user as fallback");
      getTestUserAsBackup();
      return;
    }
    
    // Get or create user with Keycloak ID - never fall back to test user for authenticated users
    const getUserData = async () => {
      if (!keycloak?.tokenParsed?.sub) {
        console.error("No Keycloak token available");
        return;
      }

      try {
        console.log("Attempting to get user with Keycloak ID:", keycloak.tokenParsed.sub);
        console.log("Making API call to:", `/api/users/by-keycloak/${keycloak.tokenParsed.sub}`);
        const response = await axios.get(`/api/users/by-keycloak/${keycloak.tokenParsed.sub}`);
        console.log("User found:", response.data);
        const fetchedUserId = response.data.id;
        setUserId(fetchedUserId);
      } catch (error) {
        console.log("Error response:", error.response);
        
        // If not found (404), create a new user
        if (error.response && error.response.status === 404) {
          console.log("User not found, creating new user");
          try {
            const userData = {
              keycloak_id: keycloak.tokenParsed.sub,
              username: keycloak.tokenParsed.preferred_username || 'User',
              email: keycloak.tokenParsed.email || 'user@example.com'
            };
            console.log("Creating user with data:", userData);
            const createResponse = await axios.post('/api/users/', userData);
            console.log("User created:", createResponse.data);
            const createdUserId = createResponse.data.id;
            setUserId(createdUserId);
          } catch (createError) {
            console.error('Error creating user:', createError);
            
            // Try to retrieve the user one more time
            try {
              console.log("Retrying user lookup after creation error");
              const retryResponse = await axios.get(`/api/users/by-keycloak/${keycloak.tokenParsed.sub}`);
              console.log("Retrieved user on retry:", retryResponse.data);
              const retryUserId = retryResponse.data.id;
              setUserId(retryUserId);
            } catch (retryError) {
              console.error('Fatal error - cannot retrieve or create user:', retryError);
              // Use fallback to test user as last resort
              console.warn('Using test user as emergency fallback');
              const testUserSuccess = await getTestUserAsBackup();
              
              if (!testUserSuccess) {
                console.error('Could not get test user either. Using keycloak ID directly as fallback.');
                setUserId(keycloak.tokenParsed.sub);
              }
            }
          }
        } else {
          console.error('Error getting user:', error);
          // Use fallback to test user
          console.warn('Using test user as fallback due to error');
          const testUserSuccess = await getTestUserAsBackup();
          
          if (!testUserSuccess) {
            console.error('Could not get test user either. Using keycloak ID directly as fallback.');
            setUserId(keycloak.tokenParsed.sub);
          }
        }
      }
    };
    
    const getTestUserAsBackup = async () => {
      try {
        console.log("Fallback: Getting test user");
        const response = await axios.get(`/api/debug-route`);
        if (response.data.test_user && response.data.test_user.id) {
          console.log("Using test user:", response.data.test_user);
          setUserId(response.data.test_user.id);
          return true;
        }
      } catch (error) {
        console.error("Failed to get test user:", error);
        return false;
      }
      return false;
    };
    
    getUserData();
  }, [keycloak]);
  
  // Load flows using practiceId
  useEffect(() => {
    if (!practiceId) return; // Wait for practiceId
    
    const loadFlows = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load flows from the API using practice_id
        const response = await axios.get(`/api/call-flows/practice/${practiceId}`);
        setFlows(response.data.map(flow => ({ // Adapt API response structure if needed
          id: flow.id,
          name: flow.name,
          description: flow.description,
          lastModified: flow.updated_at // Assuming updated_at is available
        })));
        console.log("Flows loaded from API:", response.data);

        // Optionally: Load current flow ID from localStorage (still user-specific)
        const currentFlowId = localStorage.getItem(`currentFlow-${userId}`);
        if (currentFlowId) {
          // Find the flow in the loaded list
          const currentFlow = response.data.find(f => f.id === currentFlowId);
          if (currentFlow) {
             loadFlow(currentFlow); // Pass the full flow object
          }
        }
      } catch (apiError) {
        console.error("Error loading flows from API:", apiError);
        setError("Failed to load flows from server. Loading from backup...");

        // Fallback to localStorage (using userId as before for backup consistency)
        try {
          const savedFlowsList = localStorage.getItem(`flowsList-${userId}`);
          if (savedFlowsList) {
            const parsedFlowsList = JSON.parse(savedFlowsList);
            setFlows(parsedFlowsList);
          }
          const currentFlowId = localStorage.getItem(`currentFlow-${userId}`);
          if (currentFlowId) {
            // Need to load the specific flow data from storage here too
            const flowData = localStorage.getItem(`flow-${userId}-${currentFlowId}`);
             if (flowData) {
               try {
                 const parsedFlow = JSON.parse(flowData);
                 loadFlow(parsedFlow, true); // Pass flag to indicate localStorage load
               } catch (parseError) {
                 console.error('Error parsing stored flow data:', parseError);
               }
             }
          }
        } catch (storageError) {
          console.error("Error loading flows from localStorage fallback:", storageError);
          setError("Failed to load flows from server and backup.");
        }
      }
      setLoading(false);
    };
    
    loadFlows();
  }, [practiceId, userId]); // Depend on both practiceId and userId

  // Load a specific flow (accepts full flow object or ID for storage lookup)
  const loadFlow = (flow, isFromStorage = false) => {
    try {
      let flowToLoad = flow;
      // If only ID is passed and it's from storage, load from storage
      if (typeof flow === 'string' && isFromStorage) {
         const flowData = localStorage.getItem(`flow-${userId}-${flow}`);
         if (flowData) {
            flowToLoad = JSON.parse(flowData);
         } else {
            throw new Error(`Flow data for ${flow} not found in localStorage.`);
         }
      }

      const flowDefinition = flowToLoad.flow_definition || {}; // Adjust based on actual API/storage structure
      const nodesData = flowDefinition.nodes || [];
      const edgesData = flowDefinition.edges || [];
      const systemPromptData = flowDefinition.systemPrompt || flowToLoad.system_prompt || '';

      setFlowName(flowToLoad.name || '');
      setFlowDescription(flowToLoad.description || '');
      setSystemPrompt(systemPromptData);
      
      const nodesWithCallbacks = nodesData.map(node => ({
        ...node,
        data: { ...node.data, onChange: updateNodeData }
      }));
      
      setNodes(nodesWithCallbacks);
      setEdges(edgesData);
      setNodeCount(nodesWithCallbacks.length);
      setFlowId(flowToLoad.id); // Set the current flow ID
      
      // Save current flow ID to localStorage (user-specific)
      localStorage.setItem(`currentFlow-${userId}`, flowToLoad.id);
      
      setSaveStatus({ saved: true, message: `Flow "${flowToLoad.name}" loaded successfully` });
      setTimeout(() => setSaveStatus({ saved: false, message: '' }), 3000);
    } catch (error) {
      console.error('Error loading flow:', error);
      setSaveStatus({ saved: false, message: `Error loading flow: ${error.message}` });
    }
  };
  
  // Handle connections between nodes
  const onConnect = useCallback(
    (params) => {
      // Create edge with arrow marker
      const newEdge = {
        ...params,
        type: 'smoothstep', // Use a more visually pleasing edge type
        animated: true,      // Add animation to show flow direction
        markerEnd: {         // Add arrow at the end
          type: MarkerType.ArrowClosed,
          width: 20,
          height: 20,
        },
        style: { stroke: '#4361ee', strokeWidth: 2 } // Custom styling
      };
      setEdges((eds) => addEdge(newEdge, eds));
      
      // Update save status to indicate unsaved changes
      setSaveStatus({ saved: false, message: '' });
    },
    [setEdges]
  );
  
  // Handle drag and drop from the sidebar
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);
  
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      
      const type = event.dataTransfer.getData('application/reactflow/type');
      
      if (typeof type === 'undefined' || !type) {
        return;
      }
      
      if (!reactFlowInstance) {
        return;
      }
      
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      
      const newNodeId = (nodeCount + 1).toString();
      
      let newNode = {
        id: newNodeId,
        type,
        position,
        data: { onChange: updateNodeData, id: newNodeId },
        dragHandle: '.node-header', // Allow dragging from the header
      };
      
      // Initialize based on node type
      switch (type) {
        case 'identifier':
          newNode.data = {
            ...newNode.data,
            name: `Identifier ${newNodeId}`,
            description: '',
          };
          break;
        case 'process':
          newNode.data = {
            ...newNode.data,
            name: `Process ${newNodeId}`,
            action: '',
          };
          break;
        case 'requestInfo':
          newNode.data = {
            ...newNode.data,
            infoName: '',
            question: '',
            required: true,
          };
          break;
        case 'database':
          newNode.data = {
            ...newNode.data,
            dbType: '',
            operation: '',
          };
          break;
        default:
          break;
      }
      
      setNodes((nds) => nds.concat(newNode));
      setNodeCount(nodeCount + 1);
      
      // Update save status to indicate unsaved changes
      setSaveStatus({ saved: false, message: '' });
    },
    [reactFlowInstance, nodeCount, setNodes]
  );
  
  // Update node data when user makes changes
  const updateNodeData = useCallback((value, field, nodeId) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return {
            ...node,
            data: {
              ...node.data,
              [field]: value,
            },
          };
        }
        return node;
      })
    );
    
    // Update save status to indicate unsaved changes
    setSaveStatus({ saved: false, message: '' });
  }, [setNodes]);
  
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow/type', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };
  
  const handleSaveFlow = async () => {
    try {
      setSaveStatus({ saved: false, message: 'Saving...' });
      
      if (!flowName) {
        setSaveStatus({ saved: false, message: 'Please provide a flow name' });
        return;
      }
      
      if (!practiceId) {
        setSaveStatus({ saved: false, message: 'Practice ID not available, cannot save.' });
        return;
      }
      
      // Collect flow data
      const flowData = {
        name: flowName,
        description: flowDescription,
        is_active: true,
        flow_definition: {
          nodes: reactFlowInstance.getNodes(),
          edges: reactFlowInstance.getEdges(),
          systemPrompt: systemPrompt
        },
        system_prompt: systemPrompt
      };
      
      let responseData;
      
      if (flowId) {
        // Update existing flow
        console.log('Updating flow with ID:', flowId, 'for practice:', practiceId);
        const response = await axios.put(`/api/call-flows/${flowId}`, flowData, {
          params: { practice_id: practiceId } // Include practice_id for updates too
        });
        responseData = response.data;
        console.log('Flow updated:', responseData);
      } else {
        // Create new flow
        console.log('Creating new flow for practice:', practiceId);
        const response = await axios.post('/api/call-flows/', flowData, {
          params: { practice_id: practiceId }
        });
        responseData = response.data;
        console.log('Flow created:', responseData);
        setFlowId(responseData.id);
      }
      
      // LocalStorage backup can remain user-centric if desired, or switch to practiceId
      // Using userId here for consistency with previous backup logic
      try {
        let flowsList = [];
        const savedFlowsList = localStorage.getItem(`flowsList-${userId}`); // Still using userId for key
        if (savedFlowsList) {
          flowsList = JSON.parse(savedFlowsList);
          // Check if flow already exists in the list
          const existingFlowIndex = flowsList.findIndex(f => f.id === responseData.id);
          if (existingFlowIndex >= 0) {
            // Update existing flow entry
            flowsList[existingFlowIndex] = { 
              id: responseData.id, 
              name: responseData.name,
              description: responseData.description,
              lastModified: new Date().toISOString()
            };
          } else {
            // Add new flow to list
            flowsList.push({ 
              id: responseData.id, 
              name: responseData.name,
              description: responseData.description,
              lastModified: new Date().toISOString()
            });
          }
        } else {
          // Create new flows list
          flowsList = [{ 
            id: responseData.id, 
            name: responseData.name,
            description: responseData.description,
            lastModified: new Date().toISOString()
          }];
        }
        
        // Save updated flows list
        localStorage.setItem(`flowsList-${userId}`, JSON.stringify(flowsList));
        
        // Save current flow data
        localStorage.setItem(`flow-${userId}-${responseData.id}`, JSON.stringify({
          nodes: reactFlowInstance.getNodes(),
          edges: reactFlowInstance.getEdges(),
          name: responseData.name,
          description: responseData.description,
          systemPrompt: systemPrompt
        }));
        
        // Save current flow ID
        localStorage.setItem(`currentFlow-${userId}`, responseData.id); // userId key
        
        // Update flows state
        setFlows(flowsList);
        
      } catch (storageError) {
        console.error('Error backing up flow to localStorage:', storageError);
        // Continue with success message since API save succeeded
      }
      
      // Update status
      setSaveStatus({ saved: true, message: 'Flow saved successfully!' });
      setTimeout(() => setSaveStatus({ saved: true, message: '' }), 3000);
      
      if (showSystemPrompt) {
        setShowSystemPrompt(false);
      }
    } catch (error) {
      setSaveStatus({ saved: false, message: `Error saving: ${error.response?.data?.detail || error.message}` });
      console.error('Error saving flow:', error);
    }
  };
  
  const handleNewFlow = () => {
    if (nodes.length > 1 || edges.length > 0 || flowName || flowDescription || systemPrompt) {
      if (!window.confirm('Are you sure you want to create a new flow? Any unsaved changes will be lost.')) {
        return;
      }
    }
    
    // Reset to initial state
    setNodes(initialNodes);
    setEdges([]);
    setNodeCount(1);
    setFlowName('');
    setFlowDescription('');
    setSystemPrompt('');
    setSaveStatus({ saved: false, message: '' });
    
    // Clear current flow
    localStorage.removeItem(`currentFlow-${userId}`);
  };
  
  const handleSelectFlow = (flowId) => {
    if (nodes.length > 1 || edges.length > 0) {
      if (!window.confirm('Loading a different flow will replace your current work. Any unsaved changes will be lost. Continue?')) {
        return;
      }
    }
    
    loadFlow(flowId);
    setShowFlowSelector(false);
  };
  
  const handleDeleteFlow = (flowId, event) => {
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this flow? This action cannot be undone.')) {
      return;
    }
    
    try {
      // Remove flow data
      localStorage.removeItem(`flow-${userId}-${flowId}`);
      
      // Update flows list
      const updatedFlows = flows.filter(flow => flow.id !== flowId);
      setFlows(updatedFlows);
      localStorage.setItem(`flowsList-${userId}`, JSON.stringify(updatedFlows));
      
      // If the deleted flow was the current one, reset view
      if (localStorage.getItem(`currentFlow-${userId}`) === flowId) {
        handleNewFlow();
      }
      
      setSaveStatus({ saved: true, message: 'Flow deleted successfully' });
      setTimeout(() => setSaveStatus({ saved: false, message: '' }), 3000);
    } catch (error) {
      console.error('Error deleting flow:', error);
      setSaveStatus({ saved: false, message: `Error deleting flow: ${error.message}` });
    }
  };
  
  const handleCancel = () => {
    setShowSystemPrompt(false);
  };

  // Render-time guard: wait for PracticeContext to resolve before rendering.
  if (practiceLoading) {
    return <Layout><div className="loading">Laden...</div></Layout>;
  }

  return (
    <Layout>
      <div className="call-flow-page">
        <h1 className="h1" style={{ marginBottom: 0 }}>Call Flow Builder</h1>
        <p className="user-info">User: {keycloak?.tokenParsed?.preferred_username || 'User'}</p>
        <div className="flow-container">
          <div className="side-panel">
            <div className="card node-types">
              <h3 className="h3 node-types-title">Node Types</h3>
              <div className="node-type identifier-type" draggable onDragStart={e => onDragStart(e, 'identifier')}>Identifier</div>
              <div className="node-type process-type" draggable onDragStart={e => onDragStart(e, 'process')}>Process Step</div>
              <div className="node-type request-info-type" draggable onDragStart={e => onDragStart(e, 'requestInfo')}>Request Info</div>
              <div className="node-type database-type" draggable onDragStart={e => onDragStart(e, 'database')}>Database</div>
            </div>
            <div className="card instructions">
              <h3 className="h3 instructions-title">Instructions</h3>
              <p>1. Drag node types to the canvas</p>
              <p>2. Connect nodes by clicking and dragging from a node's output port (right side) to another node's input port (left side)</p>
              <p>3. Configure each node's properties</p>
              <p>4. Set the system prompt for the LLM</p>
              <p>5. Save your flow</p>
            </div>
            <div className="flow-info-form card">
              <div className="form-group">
                <label className="form-label">Flow Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={flowName}
                  onChange={e => { setFlowName(e.target.value); setSaveStatus({ saved: false, message: '' }); }}
                  placeholder="Enter flow name"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-input"
                  value={flowDescription}
                  onChange={e => { setFlowDescription(e.target.value); setSaveStatus({ saved: false, message: '' }); }}
                  placeholder="Describe the purpose of this flow"
                  rows={3}
                />
              </div>
              <div className="flow-actions">
                <button className="button" onClick={handleNewFlow}>New Flow</button>
                <button className="button" onClick={() => setShowFlowSelector(true)}>Open Flow</button>
              </div>
            </div>
          </div>
          <div className="flow-editor">
            <div className="reactflow-wrapper" ref={reactFlowWrapper}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onInit={setReactFlowInstance}
                nodeTypes={nodeTypes}
                fitView
                deleteKeyCode="Delete"
                multiSelectionKeyCode="Control"
                snapToGrid={true}
                snapGrid={[15, 15]}
              >
                <Controls />
                <MiniMap />
                <Background color="#aaa" gap={16} />
                <Panel position="top-right">
                  <button className="button" onClick={() => setShowSystemPrompt(true)}>Set System Prompt</button>
                  <button className="button" onClick={handleSaveFlow} disabled={saveStatus.message === 'Saving...'}>
                    {saveStatus.message === 'Saving...' ? 'Saving...' : 'Save Flow'}
                  </button>
                </Panel>
              </ReactFlow>
            </div>
          </div>
        </div>
        {/* Save status message */}
        {saveStatus.message && saveStatus.message !== 'Saving...' && (
          <div className={`save-status ${saveStatus.saved ? 'success' : 'error'}`}>{saveStatus.message}</div>
        )}
        {/* System prompt modal */}
        {showSystemPrompt && (
          <div className="system-prompt-modal">
            <div className="system-prompt-content card">
              <h3 className="h3">System Prompt for LLM</h3>
              <p>Define the behavior of the LLM when handling this call flow:</p>
              <textarea
                className="form-input system-prompt-input"
                value={systemPrompt}
                onChange={e => { setSystemPrompt(e.target.value); setSaveStatus({ saved: false, message: '' }); }}
                placeholder="You are an AI assistant for a medical practice. Greet the caller politely and determine their need..."
                rows={10}
              />
              <div className="form-actions">
                <button className="button" onClick={handleSaveFlow}>Save Flow</button>
                <button className="button cancel-button" onClick={handleCancel}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {/* Flow selector modal */}
        {showFlowSelector && (
          <div className="system-prompt-modal">
            <div className="system-prompt-content card">
              <h3 className="h3">Select Flow</h3>
              {flows.length > 0 ? (
                <div className="flows-list">
                  {flows.map(flow => (
                    <div key={flow.id} className="flow-item" onClick={() => handleSelectFlow(flow.id)}>
                      <div className="flow-item-info">
                        <h4>{flow.name}</h4>
                        <p>{flow.description}</p>
                        <small>Last modified: {new Date(flow.lastModified).toLocaleString()}</small>
                      </div>
                      <button className="button delete-button small" onClick={e => handleDeleteFlow(flow.id, e)} title="Delete flow">Delete</button>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No saved flows found. Create and save a flow to see it here.</p>
              )}
              <div className="form-actions">
                <button className="button cancel-button" onClick={() => setShowFlowSelector(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CallFlowPage; 